import axios from '@/plugins/axios';
import ErrorHandle from '@/helpers/errors/errorHandle';
import enumerateArray from '@/helpers/enumerateArray';
import downloadFile from '@/helpers/downloadFile';

const initialState = {
  driversListData: {},
  driversVerificationStatuses: [],
  driversPersonalityDocs: [],
  driversLocations: [],
  driverTransactionsData: {},
  driverBalance: null,
  driverHistoryData: {},
  driverName: '',
  driverAvaliableAutos: [],
  driverAutos: [],
  driverDeleteAuto: null,
  deleteDriverAutoDialogState: false,
  blockDriver: {},
  blockDriverDialogState: false,
  configuratorList: [],
  depositList: [
    {
      id: 0,
      title: 'Нет',
    },
    {
      id: 1,
      title: 'Залоговый документ',
    },
    {
      id: 2,
      title: 'Депозит',
    },
  ],
  driverRating: {
    adequacyScore: 0,
    ageScore: 0,
    carAccidentsScore: 0,
    drivingExperienceScore: 0,
    insuranceScore: 0,
    scoreTotal: 0,
    stableScore: 0,
    carAccidents: [],
  },
};
const getters = {
  getDriversListData: (state) => state.driversListData,
  getDriversVerificationStatuses: (state) => state.driversVerificationStatuses,
  getDriversPersonalityDocs: (state) => state.driversPersonalityDocs,
  getDriversLocations: (state) => state.driversLocations,
  getDriverTransactionsData: (state) => state.driverTransactionsData,
  getDriverBalance: (state) => state.driverBalance,
  getDriverHistoryData: (state) => state.driverHistoryData,
  getDriverName: (state) => state.driverName,
  getDriverAvailableAutos: (state) => state.driverAvaliableAutos,
  getDriverAutos: (state) => state.driverAutos,
  getDriverDeleteAuto: (state) => state.driverDeleteAuto,
  getDeleteDriverAutoDialogState: (state) => state.deleteDriverAutoDialogState,
  getBlockDriver: (state) => state.blockDriver,
  getBlockDriverDialogState: (state) => state.blockDriverDialogState,
  getDepositList: (state) => state.depositList,
};

const mutations = {
  setDriversListData(state, data) {
    state.driversListData = data;
  },
  setDriversVerificationStatuses(state, data) {
    state.driversVerificationStatuses = data;
  },
  setDriversPersonalityDocs(state, data) {
    state.driversPersonalityDocs = data;
  },
  setDriversLocations(state, data) {
    state.driversLocations = data;
  },

  setDriverTransactionsData(state, data) {
    state.driverTransactionsData = data;
  },
  setDriverBalance(state, data) {
    state.driverBalance = data;
  },
  setDriverHistoryData(state, data) {
    state.driverHistoryData = data;
  },
  setDriverName(state, data) {
    state.driverName = data;
  },
  setDriverAvailableAutos(state, data) {
    state.driverAvaliableAutos = data;
  },
  setDriverAutos(state, data) {
    state.driverAutos = data;
  },
  addDriverAuto(state, data) {
    state.driverAutos.unshift(data);
  },
  setDriverDeleteAuto(state, data) {
    state.driverDeleteAuto = data;
  },
  setDeleteDriverAutoDialogState(state, data) {
    state.deleteDriverAutoDialogState = data;
  },
  deleteDriverAutoFromList(state, autoId) {
    const newDriverAutosList = state.driverAutos.filter(
      (auto) => auto.id !== autoId,
    );
    state.driverAutos = newDriverAutosList;
  },
  setBlockDriver(state, data) {
    state.blockDriver = data;
  },
  setBlockDriverDialogState(state, data) {
    state.blockDriverDialogState = data;
  },
  setConfiguratorList(state, data) {
    state.configuratorList = data;
  },
  setDriverConfigurator(state, data) {
    state.driverRating = data;
  },
};

/* eslint-disable no-unused-vars */
const actions = {
  async fetchDriversListData({ commit }, credentials) {
    try {
      commit('global/setLoader', true, { root: true });
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';

      const data = await axios.get('/drivers', {
        params: credentials,
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const driversListData = {
        ...data.data.data.drivers,
        items: enumerateArray(
          data.data.data.drivers.items,
          data.data.data.drivers.currentPage,
          data.data.data.drivers.perPage,
        ),
      };

      commit('setDriversListData', driversListData);
      commit('global/setLoader', false, { root: true });
      return driversListData;
    } catch (error) {
      commit('global/setLoader', false, { root: true });
      throw ErrorHandle(error, this);
    }
  },
  async downloadList(_, { params, name }) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('drivers', {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params: {
          ...params,
          export: 1,
        },
      });

      downloadFile(data, name);

      return { success: true };
    } catch {
      return { error: true };
    }
  },
  async fetchDriversProperties({ commit }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.get('/drivers/properties', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const {
        verificationStatus,
        personalityDoc,
        location,
      } = data.data.data.properties;

      const statuses = Object.entries(
        verificationStatus,
      ).map(([id, title]) => ({ id: +id, title }));

      commit('setDriversVerificationStatuses', statuses);
      commit('setDriversPersonalityDocs', personalityDoc);
      commit('setDriversLocations', location);
      return data;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  // eslint-disable-next-line
  async createDriver({ commit }, driverData) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.post('/drivers', driverData, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return data.data;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  // eslint-disable-next-line
  async editDriver({ commit }, driverData) {
    try {
      const { driverId } = driverData;
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.put(`/drivers/${driverId}`, driverData.data, {
        headers: {
          Authorization: `Bearer ${token}`,
          Lang: lang,
          'Content-type': 'application/json',
        },
      });

      return { success: true };
    } catch (error) {
      return { error: error.response.data.error.message };
    }
  },
  async fetchDriverTransactionsData({ commit }, credentials) {
    try {
      commit('global/setLoader', true, { root: true });
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const page = credentials.page ? credentials.page : '';
      const driverId = credentials.driverId ? credentials.driverId : '';
      const data = await axios.get(
        `/drivers/${driverId}/transactions/?page=${page}`,
        { headers: { Authorization: `Bearer ${token}`, Lang: lang } },
      );

      const transactionsData = Object.values(data.data.data.transactions);
      const allTransactionsList = transactionsData.reduce(
        (transactions, item) => {
          if (!item.inBalance) {
            transactions.push({
              ...item,
              isNumberPositive: item.isNumberPositive ? 0 : 1,
            });
          }

          transactions.push(item);
          return transactions;
        },
        [],
      );

      const list = enumerateArray(allTransactionsList);

      const driverBalance = data.data.data.balance;
      const { userId } = data.data.data;

      commit('setDriverTransactionsData', list);
      commit('setDriverBalance', driverBalance);
      commit('user/setUserId', userId, { root: true });
      commit('global/setLoader', false, { root: true });
      return list;
    } catch (error) {
      commit('global/setLoader', false, { root: true });
      throw ErrorHandle(error, this);
    }
  },
  async fetchDriverHistoryData({ commit }, credentials) {
    try {
      commit('global/setLoader', true, { root: true });
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const page = credentials.page ? credentials.page : '';
      const driverId = credentials.driverId ? credentials.driverId : '';
      const data = await axios.get(
        `/drivers/${driverId}/history/?page=${page}`,
        { headers: { Authorization: `Bearer ${token}`, Lang: lang } },
      );

      const driverHistoryData = {
        ...data.data.data.history,
        items: enumerateArray(
          data.data.data.history.items,
          data.data.data.history.currentPage,
          data.data.data.history.perPage,
        ),
      };

      commit('setDriverHistoryData', driverHistoryData);
      commit('global/setLoader', false, { root: true });
      return driverHistoryData;
    } catch (error) {
      commit('global/setLoader', false, { root: true });
      throw ErrorHandle(error, this);
    }
  },
  // eslint-disable-next-line
  async fetchDriverData({ commit }, driverId) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.get(`/drivers/${driverId}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });
      return data.data;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  async fetchDriverName({ commit }, credentials) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.get(`/drivers/${credentials.id}/name`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const driverName = data.data.data.name;

      commit('setDriverName', driverName);
      return driverName;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  async fetchDriverAvailableAutos({ commit }, credentials) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.get(
        `/drivers/${credentials.id}/cars/autocomplete/?searchString=${credentials.searchString}`,
        { headers: { Authorization: `Bearer ${token}`, Lang: lang } },
      );

      const driverAvailableAutos = data.data.data.result;

      commit('setDriverAvailableAutos', driverAvailableAutos);
      return driverAvailableAutos;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  async fetchDriverAutos({ commit }, credentials) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.get(`/drivers/${credentials.id}/cars`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const driverAutos = enumerateArray(data.data.data.cars);

      commit('setDriverAutos', driverAutos);
      return driverAutos;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  async addDriverAuto({ commit }, credentials) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.post(
        `/drivers/${credentials.driverId}/car`,
        { id: credentials.autoId },
        { headers: { Authorization: `Bearer ${token}`, Lang: lang } },
      );

      const driverAuto = data.data;

      commit('addDriverAuto', driverAuto.data);
      return driverAuto;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  async deleteDriverAuto({ commit }, credentials) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.delete(
        `/drivers/${credentials.driverId}/cars/${credentials.autoId}`,
        { headers: { Authorization: `Bearer ${token}`, Lang: lang } },
      );

      const { message } = data.data.data;

      commit('deleteDriverAutoFromList', credentials.autoId);
      return message;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  async changeDriverBlockStatus({ commit }, credentials) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.patch(
        `/drivers/${credentials.driverId}/block`,
        {},
        { headers: { Authorization: `Bearer ${token}`, Lang: lang } },
      );

      const { message } = data.data.data;

      return message;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },

  async fetchRatingConfigurator({ commit }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get('driver_rating', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      commit('setConfiguratorList', data.data.items);

      return data;
    } catch (error) {
      return error;
    }
  },

  async addRatingConfiguration(_, params) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.post('driver_rating', params, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return { success: true };
    } catch (error) {
      return { error: error.message };
    }
  },

  async updateRatingConfiguration(_, { id, params }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.put(`driver_rating/${id}`, params, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return { success: true };
    } catch (error) {
      return { error: error.message };
    }
  },

  async fetchDriverRating({ commit }, { driverId }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get(`drivers/${driverId}/rating`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      commit('setDriverConfigurator', data.data);
      return data;
    } catch (error) {
      return { error: error.message };
    }
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};
